import {
  Box,
  Typography,
  Grid,
  Card,
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  Button,
} from "@mui/material";
import {
  useForm,
  FormProvider,
  Controller,
  useFormContext,
  useFormState,
} from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { common, input } from "../../../i18n";
import InfoPopper from "../../../components/popper/InfoPopper";
import CustomDatePicker from "../../../components/input/CustomDatePicker";
import NumberFormatterUtils from "../../../utils/numberFormatterUtils";
import { BaseYearSettingTable } from "../tables/BaseYearSettingTable";
import { goalSettingService } from "../../../service/goal-setting.service";
import alertService from "../../../components/alert/alertService";
import { ApiError } from "../../../api/ApiError";
import { GoalSettingContext } from "../../../contexts/GoalSettingContext";
import { GoalPlanBaseYear } from "../../../models/GoalSetting";
import { CustomSelect2 } from "../../../components/input/CustomSelect2";
import { NumericFormat } from "react-number-format";
import CustomDatePicker2 from "../../../components/input/CustomDatePicker2";

export const truncTo2 = (n: number, d = 2) => {
  // console.log("No", n);
  return Math.trunc(n * 10 ** d) / 10 ** d;
};

type Props = {
  id?: string;
};

declare global {
  interface Window {
    __RHF_BASEYEAR__?: any;
  }
}

export const BaseYearSettingSection = ({ id }: Props) => {
  const { t } = useTranslation([common, input], { nsMode: "fallback" });
  const {
    emissionOptions,
    methodology,
    isEditMode,
    goalInfo,
    setIsPlanFetched,
    setHasUserTriggered,
    hasUserTriggered,
  } = GoalSettingContext();

  // ฟอร์มภายใน section
  const form = useForm({
    defaultValues: {
      baseYear: null as number | null,
      startYear: null as number | null,
      targetYear: null as number | null,
      baseYearEmission: 0,
      goalNetEmission: 0,
      percentReduction: 40,
      targetAbsoluteRate: true,
      rows: [] as GoalPlanBaseYear[],
    },
  });

  const { watch, setValue, getValues } = form;
  const values = watch();

  const [showSection, setShowSection] = useState(false);
  // const [hasUserChanged, setHasUserChanged] = useState(false);

  // แสดง section เฉพาะเมื่อ methodology เป็น baseYear และ emission มีข้อมูล
  useEffect(() => {
    setShowSection(methodology === "baseYear" && emissionOptions.length > 0);
  }, [methodology, emissionOptions]);

  // โหลดข้อมูลจาก goalInfo เมื่อเป็น Edit Mode
  useEffect(() => {
    if (isEditMode && goalInfo && goalInfo.methodology === "baseYear") {
      setValue("baseYear", goalInfo.baseYear, { shouldDirty: false });
      setValue("startYear", goalInfo.startYear, { shouldDirty: false });
      setValue("targetYear", goalInfo.targetYear, { shouldDirty: false });
      setValue("baseYearEmission", goalInfo.baseYearEmission, {
        shouldDirty: false,
      });
      setValue("percentReduction", goalInfo.percentReduction, {
        shouldDirty: false,
      });
      setValue("targetAbsoluteRate", goalInfo.targetAbsoluteRate, {
        shouldDirty: false,
      });
      setValue("goalNetEmission", goalInfo.goalNetEmission, {
        shouldDirty: false,
      });
      setValue("rows", goalInfo.plans as GoalPlanBaseYear[], {
        shouldDirty: false,
      });
      // console.log("netzero", goalInfo);
      // console.log("netzero", goalInfo.goalNetEmission);
      setShowSection(true);
      setIsPlanFetched(true);
    }
  }, [isEditMode, goalInfo]);

  // generateTargetYears (ปีที่หาร 5 ลงตัว)
  const generateTargetYears = (start: number) => {
    const years: number[] = [];
    const maxYear = 2050;
    for (let y = start + 1; y <= maxYear; y++) {
      if (y % 5 === 0) years.push(y);
    }
    return years;
  };

  // handle baseYear change
  const handleBaseYearChange = (date: dayjs.Dayjs | null) => {
    const year = date?.year() ?? null;
    // console.log("year", year, date);
    if (!year) return;
    setHasUserTriggered(true);
    // debugger

    const found = emissionOptions.find((e) => e.year === year);
    if (found) {
      setValue("baseYear", year, { shouldDirty: true });
      setValue("baseYearEmission", found.emission);
      setValue("startYear", year + 1);
    } else {
      // ❌ ไม่มีข้อมูล emission ในปีนั้น
      alertService.warning(
        t(
          "no-emission-data-for-selected-year",
          "No emission data found for the selected year. Please choose another year.",
        ),
      );
      setValue("baseYear", null, { shouldDirty: true });
      setValue("rows", []); // ✅ ล้างตารางทั้งหมด
    }
  };

  // หาก startYear >= targetYear → ปรับ targetYear ใหม่
  useEffect(() => {
    if (
      values.startYear &&
      values.targetYear &&
      values.startYear >= values.targetYear
    ) {
      const nextTargets = generateTargetYears(values.startYear);
      setValue("targetYear", nextTargets[0] ?? null);
    }
  }, [values.startYear]);

  useEffect(() => {
    const { baseYear, startYear, targetYear } = values;
    if (!baseYear || !startYear || !targetYear) return;

    if (isEditMode && !hasUserTriggered) return;
    if (!values.targetAbsoluteRate) return;

    const fetchPlan = async () => {
      try {
        alertService.showLoading();
        if (startYear < targetYear) {
          const res = await goalSettingService.generatePlanBaseyear({
            baseYear,
            startYear,
            targetYear,
            percentReduction: values.percentReduction, // ✅ ใช้ค่าในปัจจุบัน แต่ไม่ watch
            targetAbsoluteRate: values.targetAbsoluteRate,
            baseYearEmission: values.baseYearEmission,
          });
          alertService.closeLoading();

          setValue("rows", res?.plans || []);
          setValue("goalNetEmission", res?.goalNetEmission || 0);
          setIsPlanFetched(true);
        }
      } catch (err) {
        alertService.closeLoading();
        console.error("fetch plan error:", err);
        if (!(err instanceof ApiError)) {
          await alertService.error(t("Error-Fetching-Plan"));
        }
      }
    };

    fetchPlan();
  }, [
    values.baseYear,
    values.startYear,
    values.targetYear,
    values.targetAbsoluteRate,
  ]);

  const { isDirty, dirtyFields } = useFormState({ control: form.control });

  useEffect(() => {
    window.__RHF_BASEYEAR__ = form;
    return () => {
      delete window.__RHF_BASEYEAR__;
    };
  }, [form]);

  // useEffect(() => {
  //   console.log("isDirty:", isDirty, dirtyFields);
  // }, [isDirty, dirtyFields]);

  if (!showSection) return null;

  const minYear = emissionOptions.length
    ? Math.min(...emissionOptions.map((o) => o.year))
    : 2000;
  const maxYear = emissionOptions.length
    ? Math.max(...emissionOptions.map((o) => o.year))
    : 2050;

  return (
    <FormProvider {...form}>
      <Box mt={4} id={id}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          {t("Settings")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sx={{ display: "flex" }}>
            <Card sx={{ p: 2, flex: 1 }} variant="outlined">
              <Typography variant="h4" color="secondary" sx={{ mb: "20px" }}>
                {t("Starting-Year")}
                {/* <InfoPopper text={t("Starting-Year")} /> */}
              </Typography>

              {/* 🔹 Base Year */}
              <Grid
                container
                alignItems="center"
                spacing={2}
                sx={{ mb: "20px" }}
              >
                <Grid item xs={3}>
                  <Typography variant="body1" sx={{ color: "var(--gray1)" }}>
                    {t("Base-Year")} *
                    <InfoPopper text={t("Base-Year-Description")} />
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <FormControl fullWidth>
                    <Controller
                      name="baseYear"
                      control={form.control}
                      render={({ field: { onChange, value } }) => (
                        <CustomDatePicker2
                          info=""
                          label=""
                          defaultDate={null}
                          showInfo={false}
                          value={value ? dayjs(`${value}-01-01`) : null}
                          handleOnChange={(date) => handleBaseYearChange(date)}
                          required
                          views={["year"]}
                          format="YYYY"
                          minDate={dayjs(`${minYear}-01-01`)}
                          maxDate={dayjs(`${maxYear}-12-31`)}
                          availableYears={emissionOptions.map((e) => e.year)} // ✅ ปีที่มีข้อมูลเท่านั้น
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              {/* 🔹 Base Year Emission */}
              <Grid
                container
                alignItems="center"
                spacing={2}
                sx={{ mb: "20px" }}
              >
                <Grid item xs={3}>
                  <Typography variant="body1" sx={{ color: "var(--gray1)" }}>
                    {t("Base-Year-Emission")} *
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    value={NumberFormatterUtils.numberFormat(
                      values.baseYearEmission || 0,
                    )}
                    fullWidth
                    size="small"
                    disabled
                  />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body1" sx={{ color: "var(--dark)" }}>
                    tCO<sub>2</sub>e
                  </Typography>
                </Grid>
              </Grid>

              {/* 🔹 Start Year */}
              <Grid
                container
                alignItems="center"
                spacing={2}
                sx={{ mb: "20px" }}
              >
                <Grid item xs={3}>
                  <Typography variant="body1" sx={{ color: "var(--gray1)" }}>
                    {t("Starting-Year")} *
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <FormControl fullWidth>
                    <Controller
                      name="startYear"
                      control={form.control}
                      render={({ field: { onChange, value } }) => (
                        <CustomDatePicker2
                          info=""
                          label=""
                          showInfo={false}
                          defaultDate={null}
                          value={value ? dayjs(`${value}-01-01`) : null}
                          handleOnChange={(d) => {
                            setHasUserTriggered(true);
                            onChange(d?.year() ?? null);
                          }}
                          required
                          views={["year"]}
                          format="YYYY"
                          minDate={
                            values.baseYear
                              ? dayjs(`${values.baseYear + 1}-01-01`)
                              : dayjs("2000-01-01")
                          }
                          maxDate={dayjs("2049-12-31")}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          <Grid item xs={6} sx={{ display: "flex" }}>
            <Card sx={{ p: 2, flex: 1 }} variant="outlined">
              <Typography variant="h4" color="secondary" sx={{ mb: "20px" }}>
                {t("Target-Goal")}
              </Typography>

              {/* 🔹 Target Year */}
              <Grid
                container
                alignItems="center"
                spacing={2}
                sx={{ mb: "20px" }}
              >
                <Grid item xs={3}>
                  <Typography variant="body1" sx={{ color: "var(--gray1)" }}>
                    {t("Target-Year")} *
                    <InfoPopper text={t("Target-Year-Description")} />
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <CustomSelect2
                    name="targetYear"
                    control={form.control}
                    label={t("Target-Year")}
                    info=""
                    placeholder={t("Select")}
                    rules={{
                      onChange(event) {
                        setValue("targetYear", Number(event.target.value));
                        setHasUserTriggered(true);
                      },
                    }}
                    required
                    listMenuItem={
                      values.startYear
                        ? generateTargetYears(values.startYear).map((year) => ({
                            nameEN: String(year),
                            nameTH: String(year),
                            value: year,
                          }))
                        : []
                    }
                    showLabelInput={false}
                  />
                </Grid>
              </Grid>
              {/* Goal Net Emission */}
              <Grid
                container
                alignItems="center"
                spacing={2}
                sx={{ mb: "20px" }}
              >
                <Grid item xs={3}>
                  <Typography variant="body1" sx={{ color: "var(--gray1)" }}>
                    {t("Goal-Net-Emission")} *
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <NumericFormat
                    value={values.goalNetEmission}
                    thousandSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    displayType="input" // ใช้ input เพื่อให้ format ลงในช่อง
                    customInput={TextField}
                    fullWidth
                    size="small"
                    disabled
                  />
                </Grid>
              </Grid>
              {/* Percent Reduction */}
              <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={3}>
                  <Typography variant="body1" sx={{ color: "var(--gray1)" }}>
                    % {t("Reduction")} *
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <NumericFormat
                    value={values.percentReduction ?? ""}
                    thousandSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={false}
                    allowLeadingZeros={false}
                    customInput={TextField}
                    onFocus={(e) => e.target.select()}
                    fullWidth
                    size="small"
                    disabled={!values.targetAbsoluteRate}
                    isAllowed={(values) => {
                      const { floatValue } = values;
                      return (
                        floatValue == null ||
                        (floatValue >= 0 && floatValue <= 100)
                      );
                    }}
                    onValueChange={(values) => {
                      const { floatValue } = values;
                      if (floatValue == null) {
                        setValue("percentReduction", 0, { shouldDirty: true });
                      } else {
                        setValue("percentReduction", floatValue, {
                          shouldDirty: true,
                        });
                      }
                    }}
                    onBlur={async (e) => {
                      const raw = e.target.value.replace("%", "").trim();

                      // 🔸 ไม่มีค่า
                      if (raw === "") {
                        alertService.error(t("value-is-required"));
                        setValue("percentReduction", 0, { shouldDirty: true });
                        return;
                      }

                      const val = parseFloat(raw);

                      // 🔸 ไม่ใช่ตัวเลข
                      if (isNaN(val)) {
                        alertService.error(t("only-numbers-are-allowed"));
                        setValue("percentReduction", 0, { shouldDirty: true });
                        return;
                      }

                      // 🔸 อยู่นอกช่วง 0–100
                      if (val < 0 || val > 100) {
                        alertService.error(
                          t("value-must-be-between-0-and-100"),
                        );
                        setValue("percentReduction", 0, { shouldDirty: true });
                        return;
                      }

                      // ✅ ผ่านทุกเงื่อนไข → ปัดทศนิยม
                      const rounded = parseFloat(val.toFixed(2));
                      setValue("percentReduction", rounded, {
                        shouldDirty: true,
                      });

                      try {
                        alertService.showLoading();
                        if (!dirtyFields.targetAbsoluteRate) {
                          // ✅ ยิง API โดยตรง
                          const res =
                            await goalSettingService.generatePlanBaseyear({
                              baseYear: values.baseYear,
                              startYear: values.startYear,
                              targetYear: values.targetYear,
                              percentReduction: rounded,
                              targetAbsoluteRate: values.targetAbsoluteRate,
                              baseYearEmission: values.baseYearEmission,
                            });

                          alertService.closeLoading();
                          setValue("rows", res?.plans || []);
                          setValue(
                            "goalNetEmission",
                            res?.goalNetEmission || 0,
                          );
                          // setIsPlanFetched(true);

                          alertService.mixin(t("Updated-Success"));
                        }
                      } catch (err) {
                        alertService.closeLoading();
                        console.error("Error updating plan:", err);
                        if (!(err instanceof ApiError)) {
                          await alertService.error(t("Error-Updating-Plan"));
                        }
                      }
                    }}
                    onKeyDown={(event) => event.stopPropagation()}
                    onKeyUp={(event: React.KeyboardEvent) => {
                      if (event.key === "Enter") {
                        (document.activeElement as HTMLElement).blur(); // ➡ trigger onBlur
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.targetAbsoluteRate}
                        onChange={(e) => {
                          setValue("targetAbsoluteRate", e.target.checked);
                          setHasUserTriggered(true);
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1">
                        {t("Absolute-Rate")}
                        <InfoPopper text={t("Absolute-Rate-Description")} />
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>

        {/* 🔹 ตาราง */}
        <Box mt={3}>
          <BaseYearSettingTable />
        </Box>
      </Box>
    </FormProvider>
  );
};
